import * as THREE from 'three';
import { puff } from '../../look/noise.js';
import { SpriteCloud } from '../sprites.js';
import type { WorldBuilder } from '../world.js';

/** The inner cone's colour as a multiple of the outer's (the lab's: 0.12/0.28/0.95 → 0.25/0.5/1.1). */
const INNER = new THREE.Color(0.25 / 0.12, 0.5 / 0.28, 1.1 / 0.95);

/**
 * A Bunsen-type flame (moved from lab.ts): an open cone and a smaller one inside it, added, flickering,
 * with a glow halo. `level` (0–1) grows it from nothing; gone at 0. Proportions are the lab's at
 * `height` 0.1 and `radius` 0.018.
 */
export function buildFlame(
  world: WorldBuilder,
  options: {
    at: THREE.Vector3;
    radius: number;
    height: number;
    colour: THREE.Color;
    opacity: number;
    glow: number;
    glowColour: THREE.Color;
    name: string;
  },
): { group: THREE.Group; update(elapsed: number, level: number): void } {
  const { radius, height } = options;
  const outer = new THREE.Mesh(
    new THREE.ConeGeometry(radius, height, 12, 1, true).translate(0, height / 2, 0),
    new THREE.MeshBasicMaterial({
      color: options.colour,
      transparent: true,
      opacity: options.opacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  const inner = new THREE.Mesh(
    new THREE.ConeGeometry(radius / 2, height * 0.4, 10).translate(0, height * 0.2, 0),
    new THREE.MeshBasicMaterial({
      color: options.colour.clone().multiply(INNER),
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  const group = new THREE.Group();
  group.add(outer, inner);
  group.position.copy(options.at);
  group.name = options.name;
  world.addAir(group);
  let burning = 0;
  if (options.glow > 0) {
    const scale = height / 0.1;
    world.addHalo({
      on: group,
      centre: new THREE.Vector3(0, 0.05 * scale, 0),
      radius: 0.06 * scale,
      colour: options.glowColour.clone(),
      flicker: (t) => options.glow * (0.9 + 0.1 * Math.sin(t * 29)) * burning,
    });
  }
  return {
    group,
    update(elapsed, level) {
      burning = level;
      const f = 1 + 0.08 * Math.sin(elapsed * 23) + 0.05 * Math.sin(elapsed * 37 + 1);
      group.visible = level > 0.01;
      group.scale.set(level, f * level, level);
    },
  };
}

/**
 * Steam over glassware (moved from lab.ts): puffs rising in turn, wobbling sideways and drifting
 * towards +z, each growing and fading. `level` (0–1) is how much there is; gone at 0.
 */
export function buildSteam(
  world: WorldBuilder,
  options: {
    at: THREE.Vector3;
    count: number;
    size: readonly [number, number];
    height: readonly [number, number];
    seconds: number;
    colour: THREE.ColorRepresentation;
    opacity: number;
    name: string;
  },
): { mesh: SpriteCloud['mesh']; update(elapsed: number, level: number): void } {
  const steam = new SpriteCloud(options.count, {
    map: world.codeTexture(puff(32, 9)),
    colour: options.colour,
    opacity: options.opacity,
    name: options.name,
  });
  world.addAir(steam.mesh);
  const p = new THREE.Vector3();
  const [start, end] = options.size;
  const [low, high] = options.height;
  return {
    mesh: steam.mesh,
    update(elapsed, level) {
      steam.mesh.visible = level > 0.01;
      if (!steam.mesh.visible) return;
      for (let i = 0; i < options.count; i++) {
        const u = (((elapsed / options.seconds + i / options.count) % 1) + 1) % 1;
        p.set(
          options.at.x + Math.sin(u * 5 + i) * 0.03,
          options.at.y + low + u * (high - low),
          options.at.z + 0.1 + u * 0.08,
        );
        steam.set(
          i,
          p,
          start + u * (end - start),
          Math.sin(u * Math.PI) * Math.min(1, level),
          u * 2 + i,
        );
      }
      steam.commit();
    },
  };
}
